const OPFS_STATE_FILE_NAME = "taskguard.vaults.encrypted.v1.json";
const CONNECTION_STRING_PREFIX = "taskguard-share-v1";
const ENCRYPTION_VERSION = 1;
const PBKDF2_ITERATIONS = 250_000;

export type EncryptedJsonPayload = {
  version: 1;
  salt: string;
  iv: string;
  ciphertext: string;
};

export type VaultConnectionPayload = {
  vaultId: string;
  password: string;
  name: string;
};

type ConnectionStringEnvelope = {
  version: 1;
  secret: string;
  payload: EncryptedJsonPayload;
};

type OpfsWritable = {
  close: () => Promise<void>;
  write: (data: string) => Promise<void>;
};

type OpfsFileHandle = {
  createWritable: () => Promise<OpfsWritable>;
  getFile: () => Promise<File>;
};

type OpfsDirectoryHandle = {
  getFileHandle: (name: string, options?: { create?: boolean }) => Promise<OpfsFileHandle>;
};

type OpfsStorageManager = StorageManager & {
  getDirectory?: () => Promise<OpfsDirectoryHandle>;
};

function getWebCrypto() {
  if (typeof globalThis !== "object" || globalThis.crypto == null) {
    throw new Error("Web Crypto API is unavailable in this browser.");
  }

  return globalThis.crypto;
}

function getSubtleCrypto() {
  const webCrypto = getWebCrypto();

  if (webCrypto.subtle == null) {
    throw new Error("SubtleCrypto is unavailable. Open the app on localhost or over HTTPS.");
  }

  return webCrypto.subtle;
}

async function getOpfsRoot() {
  if (typeof navigator === "undefined") {
    throw new Error("Origin Private File System is unavailable outside the browser.");
  }

  const storageManager = navigator.storage as OpfsStorageManager | undefined;

  if (storageManager?.getDirectory == null) {
    throw new Error("Origin Private File System is unavailable in this browser.");
  }

  return storageManager.getDirectory();
}

function isNotFoundError(error: unknown) {
  return (
    error instanceof DOMException
      ? error.name === "NotFoundError"
      : typeof error === "object" &&
          error != null &&
          "name" in error &&
          (error as { name?: string }).name === "NotFoundError"
  );
}

async function getPersistedStateFileHandle(create: boolean) {
  const root = await getOpfsRoot();
  return root.getFileHandle(OPFS_STATE_FILE_NAME, { create });
}

async function readPersistedStatePayload() {
  try {
    const fileHandle = await getPersistedStateFileHandle(false);
    const file = await fileHandle.getFile();
    const raw = await file.text();

    if (raw.trim().length === 0) {
      return null;
    }

    return JSON.parse(raw) as EncryptedJsonPayload;
  } catch (error) {
    if (isNotFoundError(error)) {
      return null;
    }

    throw error;
  }
}

function toBase64Url(bytes: Uint8Array) {
  let binary = "";

  for (const value of bytes) {
    binary += String.fromCharCode(value);
  }

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
}

function fromBase64Url(value: string) {
  const base64 = value.replace(/-/g, "+").replace(/_/g, "/");
  const padding = "=".repeat((4 - (base64.length % 4 || 4)) % 4);
  const binary = atob(base64 + padding);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return bytes;
}

function encodeJson(value: unknown) {
  return new TextEncoder().encode(JSON.stringify(value));
}

function decodeJson<T>(bytes: Uint8Array) {
  return JSON.parse(new TextDecoder().decode(bytes)) as T;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

async function deriveKey(secret: string, salt: Uint8Array, usage: KeyUsage[]) {
  const subtle = getSubtleCrypto();
  const baseKey = await subtle.importKey(
    "raw",
    new TextEncoder().encode(secret),
    "PBKDF2",
    false,
    ["deriveKey"],
  );

  return subtle.deriveKey(
    {
      name: "PBKDF2",
      hash: "SHA-256",
      salt: toArrayBuffer(salt),
      iterations: PBKDF2_ITERATIONS,
    },
    baseKey,
    {
      name: "AES-GCM",
      length: 256,
    },
    false,
    usage,
  );
}

function createRandomSecret() {
  const bytes = getWebCrypto().getRandomValues(new Uint8Array(32));
  return toBase64Url(bytes);
}

export async function encryptJsonWithSecret<T>(
  value: T,
  secret: string,
): Promise<EncryptedJsonPayload> {
  const webCrypto = getWebCrypto();
  const subtle = getSubtleCrypto();
  const salt = webCrypto.getRandomValues(new Uint8Array(16));
  const iv = webCrypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(secret, salt, ["encrypt"]);
  const ciphertext = await subtle.encrypt(
    {
      name: "AES-GCM",
      iv,
    },
    key,
    encodeJson(value),
  );

  return {
    version: ENCRYPTION_VERSION,
    salt: toBase64Url(salt),
    iv: toBase64Url(iv),
    ciphertext: toBase64Url(new Uint8Array(ciphertext)),
  };
}

export async function decryptJsonWithSecret<T>(
  payload: EncryptedJsonPayload,
  secret: string,
): Promise<T> {
  if (payload.version !== ENCRYPTION_VERSION) {
    throw new Error("Unsupported encrypted payload version.");
  }

  const salt = fromBase64Url(payload.salt);
  const iv = fromBase64Url(payload.iv);
  const ciphertext = fromBase64Url(payload.ciphertext);
  const key = await deriveKey(secret, salt, ["decrypt"]);
  const plainBuffer = await getSubtleCrypto().decrypt(
    {
      name: "AES-GCM",
      iv,
    },
    key,
    ciphertext,
  );

  return decodeJson<T>(new Uint8Array(plainBuffer));
}

export async function hasPersistedVaultState() {
  return (await readPersistedStatePayload()) != null;
}

export async function savePersistedVaultState<T>(value: T, pin: string) {
  const payload = await encryptJsonWithSecret(value, pin);
  const fileHandle = await getPersistedStateFileHandle(true);
  const writable = await fileHandle.createWritable();

  await writable.write(JSON.stringify(payload));
  await writable.close();
}

export async function loadPersistedVaultState<T>(pin: string): Promise<T> {
  const payload = await readPersistedStatePayload();

  if (payload == null) {
    throw new Error("No encrypted vault state was found in OPFS.");
  }

  return decryptJsonWithSecret<T>(payload, pin);
}

export async function createVaultConnectionString(payload: VaultConnectionPayload) {
  const secret = createRandomSecret();
  const encrypted = await encryptJsonWithSecret(payload, secret);
  const envelope: ConnectionStringEnvelope = {
    version: ENCRYPTION_VERSION,
    secret,
    payload: encrypted,
  };

  return `${CONNECTION_STRING_PREFIX}.${toBase64Url(encodeJson(envelope))}`;
}

export async function parseVaultConnectionString(value: string): Promise<VaultConnectionPayload> {
  const trimmed = value.trim();
  const prefix = `${CONNECTION_STRING_PREFIX}.`;

  if (!trimmed.startsWith(prefix)) {
    throw new Error("The connection string format is not valid.");
  }

  const encodedEnvelope = trimmed.slice(prefix.length);
  const envelope = decodeJson<ConnectionStringEnvelope>(fromBase64Url(encodedEnvelope));

  if (envelope.version !== ENCRYPTION_VERSION) {
    throw new Error("Unsupported connection string version.");
  }

  return decryptJsonWithSecret<VaultConnectionPayload>(envelope.payload, envelope.secret);
}
